<template>
  <div :class="['combo-input-wrapper', { 'is-invalid': hasError }]">
    <input
      ref="input"
      v-model="inputValue"
      type="text"
      :class="inputClass"
      :placeholder="placeholder"
      :disabled="disabled"
      autocomplete="off"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    />
    <div 
      v-if="showDropdown && filteredOptions.length > 0"
      class="dropdown-list"
      ref="dropdown"
    >
      <div
        v-for="(option, index) in filteredOptions"
        :key="getOptionValue(option)"
        :class="[
          'dropdown-item',
          { 'highlighted': index === highlightedIndex }
        ]"
        @mousedown.prevent="selectOption(option)"
        @mouseenter="highlightedIndex = index"
      >
        {{ getOptionText(option) }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SimpleComboBox',
  props: {
    value: {
      type: [String, Object],
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: '選択してください'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      inputValue: '',
      showDropdown: false,
      highlightedIndex: -1,
      isFocused: false
    }
  },
  computed: {
    inputClass() {
      return [
        'form-control',
        {
          'is-invalid': this.hasError,
          'border-red': this.hasError
        }
      ]
    },
    filteredOptions() {
      if (!this.inputValue || this.inputValue.trim() === '') {
        return this.options
      }
      const query = this.inputValue.toLowerCase().trim()
      return this.options.filter(option => {
        const text = this.getOptionText(option).toLowerCase()
        return text.includes(query)
      })
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.inputValue = newValue || ''
      }
    }
  },
  methods: {
    getOptionValue(option) {
      return typeof option === 'object' ? option.value : option
    },

    getOptionText(option) {
      return typeof option === 'object' ? option.text : option
    },

    onInput(event) {
      this.inputValue = event.target.value
      this.showDropdown = this.isFocused && this.filteredOptions.length > 0
      this.highlightedIndex = -1
      this.$emit('input', this.inputValue)
      this.$emit('search', this.inputValue)
    },

    onFocus() {
      this.isFocused = true
      this.showDropdown = this.filteredOptions.length > 0
      this.highlightedIndex = -1
    },

    onBlur() {
      // 少し遅延させてドロップダウンクリックを処理
      setTimeout(() => {
        this.isFocused = false
        this.showDropdown = false
        this.validateInput()
      }, 150)
    },

    onKeydown(event) {
      if (!this.showDropdown) return

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.moveHighlight(1)
          break
        case 'ArrowUp':
          event.preventDefault()
          this.moveHighlight(-1)
          break
        case 'Enter':
          event.preventDefault()
          if (this.highlightedIndex >= 0) {
            this.selectOption(this.filteredOptions[this.highlightedIndex])
          }
          break
        case 'Escape':
          this.showDropdown = false
          this.$refs.input.blur()
          break
      }
    },

    moveHighlight(direction) {
      const maxIndex = this.filteredOptions.length - 1
      this.highlightedIndex += direction

      if (this.highlightedIndex > maxIndex) {
        this.highlightedIndex = 0
      } else if (this.highlightedIndex < 0) {
        this.highlightedIndex = maxIndex
      }
    },

    selectOption(option) {
      const value = this.getOptionValue(option)
      this.inputValue = this.getOptionText(option)
      this.showDropdown = false
      this.$emit('input', value)
      this.$emit('change', value)
    },

    validateInput() {
      if (this.inputValue && this.options.length > 0) {
        const validValues = this.options.map(option => this.getOptionText(option))
        if (!validValues.includes(this.inputValue)) {
          this.inputValue = ''
          this.$emit('input', '')
          this.$emit('validation-error', '存在しない値は選択できません')
        }
      }
    }
  }
}
</script>

<style scoped>
.combo-input-wrapper {
  position: relative;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 0.375rem 0.75rem;
  cursor: pointer;
  color: #495057;
  background-color: #fff;
  border: none;
  text-align: left;
  width: 100%;
  display: block;
}

.dropdown-item:hover,
.dropdown-item.highlighted {
  background-color: #007bff;
  color: #fff;
}

.dropdown-item:active {
  background-color: #0056b3;
  color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/form/SimpleComboBox.vue